import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import HoverTooltip from '@/Components/HoverTooltip'
import { appealRank } from '@/utils/facilities'
import { FloobitSymbol } from '@/Components/Icons/Floobit'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

// Levels use Roman numerals to match the app's tier convention.
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V']
const roman = (n: number) => ROMAN[n] || String(n)

// Concrete per-level perk copy (mirrors the backend FACILITY effect curves;
// keep in sync if the curves are retuned). '' = no active bonus at that level.
const PERK: Record<string, string[]> = {
  training:    ['', 'Young players develop a little faster and reach more of their ceiling', 'Young players develop faster and reach more of their ceiling', 'Young players develop notably faster, and fewer prospects stall', 'Young players develop much faster, and far fewer prospects stall', 'Young players develop better than under the best coach without one'],
  locker_room: ['', '+0.7% team rating every game, setbacks hit 10% softer', '+1.4% team rating every game, setbacks hit 20% softer', '+2.1% team rating every game, setbacks hit 30% softer', '+2.8% team rating every game, setbacks hit 40% softer', '+3.5% team rating every game, setbacks hit half as hard'],
  recovery:    ['', 'Players tire 15% slower', 'Players tire 35% slower', 'Players tire 55% slower', 'Players tire 75% slower', 'Players stay nearly fresh all season (90% slower)'],
  scouting:    ['', 'GM judges players a little better', 'GM judges players better and spots some hidden prospects', 'GM judges players notably better and spots more hidden prospects', 'GM judges players much better and sees through most hype', 'Sees through the hype: spots hidden gems and avoids busts'],
  stadium:     ['', '+0.8% team rating at home, +8% gate revenue', '+1.6% team rating at home, +16% gate revenue', '+2.4% team rating at home, +26% gate revenue', '+3.2% team rating at home, +38% gate revenue', '+4% team rating at home, +50% gate revenue'],
}
const perkAt = (key: string, lvl: number) => (PERK[key] || [])[lvl] || ''
const SHORT_FAC: Record<string, string> = {
  training: 'Train', locker_room: 'Locker', recovery: 'Recov', scouting: 'Scout', stadium: 'Stadium',
}
// Quippy flavor per facility per level (0-5). Pure personality, no mechanics.
const QUIP: Record<string, string[]> = {
  training: [
    'A field, some cones, and hope.',
    'A squat rack and a stern whistle.',
    'Real weights, real treadmills, real sweat.',
    'Position coaches and film rooms that work.',
    'Pro-grade everything. Gains all but guaranteed.',
    'Sports science so advanced the other teams are suspicious.',
  ],
  locker_room: [
    'Just hooks on a wall in a room.',
    'Broken benches, communal lockers, and one flickering light.',
    'Carpet, lockers, and a faint smell of sweat.',
    'Comfy chairs and a shower that stays hot.',
    'Lounge, game room, and a well stocked snack bar.',
    'A clubhouse nobody wants to leave.',
  ],
  recovery: [
    'Ice borrowed from the concession stand.',
    'Ice baths and a great deal of tape.',
    'A trainer who knows everyone by name.',
    'Massage tables and a hot tub that works.',
    'Cryo chambers and oxygen therapy.',
    'Players heal faster than physics allows.',
  ],
  scouting: [
    'One guy with a hunch.',
    'Binoculars and a battered clipboard.',
    'A few scouts and a serious coffee budget.',
    'Regional scouts and a well organized spreadsheet.',
    'Analysts with many large monitors.',
    'Know who will be a star before they\'re born.',
  ],
  stadium: [
    'Home games happen somewhere, technically.',
    'Bleachers, a hot dog cart, and big dreams.',
    'A roof. Most of one, anyway.',
    'A real stadium with real noise.',
    'Bright lights and a proper wall of sound.',
    'A cathedral to the sport.',
  ],
}
export const quipAt = (key: string, lvl: number) => (QUIP[key] || [])[lvl] || ''
const TIER_SHORT: Record<string, string> = {
  MEGA_MARKET: 'MEGA', LARGE_MARKET: 'LARGE', MID_MARKET: 'MID', SMALL_MARKET: 'SMALL',
}
const TIER_COLOR: Record<string, string> = {
  MEGA_MARKET: '#a78bfa', LARGE_MARKET: '#3b82f6', MID_MARKET: '#2dd4bf', SMALL_MARKET: '#f97316',
}
const FUND_AMOUNTS = [25, 50, 100]
const BUILD = '#a78bfa'   // project / construction purple
const hex = (c: string, a: number) => `${c}${Math.round(a * 255).toString(16).padStart(2, '0')}`
// flavor-text treatment for the quips (italic + warm tone + a quote bar)
const QUIP_STYLE: React.CSSProperties = {
  fontSize: '12px', fontStyle: 'italic', color: '#caa46a', lineHeight: 1.35,
  borderLeft: '2px solid #3a4456', paddingLeft: '8px',
}

// scoped animations + hover states (inline styles can't do either)
const FAC_CSS = `
@keyframes facStripes { to { background-position: 36px 0; } }
.facChip { font: inherit; cursor: pointer; transition: all .12s; }
.facChip:disabled { cursor: not-allowed; }
.facChip:hover:not(:disabled) { border-color:#3b82f6 !important; background:#1d3654 !important; color:#bfdbfe !important; box-shadow:0 0 10px rgba(59,130,246,.28); }
.facRow { transition: background .12s; }
.facRow:hover { background:#16263d !important; }
.facStripes { background-image: repeating-linear-gradient(45deg,#a78bfa 0 9px,#8b6ef0 9px 18px); background-size:36px 36px; animation: facStripes .9s linear infinite; }
`

// ── types ─────────────────────────────────────────────────────────────────
interface Facility { key: string; name: string; level: number; maxLevel: number; effect: string; upgrading: boolean; builtThisYear?: boolean; upkeepCost: number; upkeepFunded: number; upgradeCost: number }
// buildsAt: when a fully funded bar builds. 'now' = the moment it fills; 'payment_run' = the
// facility already had its upgrade this year from the payment run; 'next_season' = fans already
// built it this season, or this offseason's payment run has happened.
interface Project { id: number; facilityKey: string; kind: string; targetLevel: number; cost: number; funded: number; rank?: number | null; buildsAt?: 'now' | 'payment_run' | 'next_season' }
interface PayrollOutlook { owedNextSeason: number; projectedBalance: number; shortBy: number }
interface TeamFacilities {
  teamId: number; treasury: number; appeal: number; shareUnit: number; facilities: Facility[]; projects: Project[]; order?: string[]
  /** This season's salaries, paid first at the payment run. */
  payroll?: number
  /** In season only: whether the team is on course to fall short of next season's payroll. */
  payrollOutlook?: PayrollOutlook | null
  salaryEnabled?: boolean
}
interface RankFacility { key: string; name: string; level: number; maxLevel: number; maxed: boolean; cost: number; upkeep: number }
interface LeagueTeam { id: number; name: string; city: string; abbr: string; color: string; appeal: number; levels: Record<string, number>; fanCount: number; activeFanCount?: number; marketTier: string }

// ── small UI bits ─────────────────────────────────────────────────────────
function FundChips({ onFund, balance, max, topGap = 8, allowCustom = false }: { onFund: (amt: number) => void; balance: number; max: number; topGap?: number; allowCustom?: boolean }) {
  const [custom, setCustom] = useState('')
  const customAmt = Math.min(parseInt(custom, 10) || 0, max, balance)
  const submitCustom = () => { if (customAmt > 0) { onFund(customAmt); setCustom('') } }
  const chipStyle = (disabled: boolean): React.CSSProperties => ({
    fontSize: '12px', fontWeight: 700, borderRadius: '6px', padding: '3px 10px',
    border: `1px solid ${disabled ? '#1e293b' : '#2f4a6b'}`, background: disabled ? 'transparent' : '#15293f',
    color: disabled ? '#475569' : '#93c5fd',
  })
  return (
    <div style={{ display: 'flex', gap: '5px', marginTop: topGap, flexWrap: 'wrap', alignItems: 'center' }}>
      {FUND_AMOUNTS.map(a => {
        const amt = Math.min(a, max)
        const disabled = balance < amt || max <= 0
        return (
          <button key={a} className="facChip" onClick={() => !disabled && onFund(amt)} disabled={disabled}
                  style={{ ...chipStyle(disabled), display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            +<FloobitSymbol size={11} color="currentColor" />{a}
          </button>
        )
      })}
      {allowCustom && (
        <>
          <input
            type="number" min={1} value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitCustom() }}
            placeholder="Custom"
            style={{ width: '74px', padding: '3px 8px', fontSize: '12px', fontFamily: 'inherit',
              background: '#0f172a', color: '#e2e8f0', border: '1px solid #2f4a6b', borderRadius: '6px', outline: 'none' }}
          />
          <button className="facChip" disabled={customAmt <= 0} onClick={submitCustom} style={chipStyle(customAmt <= 0)}>Add</button>
        </>
      )}
    </div>
  )
}

/** The Treasury's job in one sentence, shared by the Facilities readout and the
 *  team page's summary strip so the two never describe different rules. */
export const treasuryTip = (salaries: boolean) => salaries
  ? 'Floobits your fanbase has banked. After the free agent draft it pays player salaries first, then facility upkeep, then builds projects in your fans\' ranked order.'
  : 'Floobits your fanbase has banked. After the free agent draft it pays facility upkeep first, then builds projects in your fans\' ranked order.'

/** On course to fall short of next season's payroll: the season-end check will cut
 *  players to close the gap unless the Treasury grows. */
function PayrollWarning({ shortBy, owed }: { shortBy: number; owed: number }) {
  return (
    <HoverTooltip
      color="#f87171"
      text={`Next season's contracts cost ${owed.toLocaleString()} Floobits. At season end, a team that can't cover them has to cut players, and the free agent draft fills the slots. This counts the gate revenue still to come and what fans' season-end deposits would bring at today's balances.`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '9px',
        background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.45)', color: '#fecaca', fontSize: '12.5px' }}>
        <span style={{ fontWeight: 800, color: '#f87171', textTransform: 'uppercase', letterSpacing: '.06em', fontSize: '11px' }}>Payroll short</span>
        <span>
          Short by <b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{shortBy.toLocaleString()}</b>. Fund the team or lose a player at season end.
        </span>
      </div>
    </HoverTooltip>
  )
}

// ── main ──────────────────────────────────────────────────────────────────
export interface FrontOfficeSummary {
  treasury: number
  appeal: number
  marketTier: string
  teamColor?: string
  payroll?: number
  salaryEnabled?: boolean
}

interface FacilitiesProps {
  /** 'band' drops the stat panel (the host draws it in its own summary strip),
   *  squares the chrome to match a radius-0 page, and folds the league-wide
   *  comparison away behind a toggle — a league table is not a your-team fact
   *  and it was the biggest thing in the section. */
  variant?: 'page' | 'band'
  /** Reports treasury/appeal/market up to the host so the summary strip can be
   *  drawn without a second identical fetch. */
  onSummary?: (s: FrontOfficeSummary) => void
}

const FacilitiesSection: React.FC<FacilitiesProps> = ({ variant = 'page', onSummary }) => {
  const band = variant === 'band'
  const { user, getToken, updateFloobits } = useAuth()
  const favId = (user as any)?.favoriteTeamId ?? null
  const [data, setData] = useState<TeamFacilities | null>(null)
  const [rankFacs, setRankFacs] = useState<RankFacility[]>([])
  const [teamOrder, setTeamOrder] = useState<string[]>([])
  const [ballots, setBallots] = useState(0)
  const [myRanking, setMyRanking] = useState<string[] | null>(null)
  const [league, setLeague] = useState<LeagueTeam[]>([])
  const [catalog, setCatalog] = useState<Record<string, string>>({})
  const [balance, setBalance] = useState<number>((user as any)?.floobits ?? 0)
  const [pct, setPct] = useState<number>((user as any)?.teamFundingPct ?? 25)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [leagueTab, setLeagueTab] = useState<'facilities' | 'fanbase'>('facilities')
  const [leagueOpen, setLeagueOpen] = useState(false)

  const load = useCallback(async () => {
    const reqs: Promise<any>[] = [
      fetch(`${API_BASE}/league/facilities`).then(r => r.json()).catch(() => null),
    ]
    if (favId) {
      reqs.push(fetch(`${API_BASE}/teams/${favId}/facilities`).then(r => r.json()).catch(() => null))
      const tok = await getToken().catch(() => null)
      reqs.push(fetch(`${API_BASE}/teams/${favId}/facilities/ranking`, tok ? { headers: { Authorization: `Bearer ${tok}` } } : {}).then(r => r.json()).catch(() => null))
    }
    const [lg, fac, rank] = await Promise.all(reqs)
    if (lg?.success) { setLeague(lg.data.teams || []); setCatalog(lg.data.facilityCatalog || {}) }
    if (fac?.success) setData(fac.data)
    if (rank?.success) {
      setRankFacs(rank.data.facilities || [])
      setTeamOrder(rank.data.order || [])
      setBallots(rank.data.ballots || 0)
      setMyRanking(rank.data.myRanking ?? null)
    }
    setLoading(false)
  }, [favId, getToken])

  useEffect(() => { load() }, [load])
  useEffect(() => { setBalance((user as any)?.floobits ?? 0) }, [user])

  // Hand the headline numbers to the host rather than making it refetch them.
  useEffect(() => {
    if (!data || !onSummary) return
    const mine = league.find(x => x.id === data.teamId)
    onSummary({
      treasury: data.treasury,
      appeal: data.appeal,
      marketTier: mine?.marketTier || 'MID_MARKET',
      teamColor: mine?.color,
      payroll: data.payroll,
      salaryEnabled: data.salaryEnabled,
    })
  }, [data, league, onSummary])

  const contribute = useCallback(async (amount: number, target: string, extra: Record<string, any> = {}) => {
    if (!favId || busy || amount <= 0) return
    setBusy(true)
    try {
      const tok = await getToken()
      const resp = await fetch(`${API_BASE}/teams/${favId}/facilities/contribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ amount, target, ...extra }),
      })
      const j = await resp.json()
      if (resp.ok) {
        const nb = j.data?.newBalance ?? balance - amount
        setBalance(nb)
        updateFloobits(nb)   // sync the global (navbar) Floobit total immediately
        await load()
      }
    } finally { setBusy(false) }
  }, [favId, busy, getToken, balance, load, updateFloobits])

  const setAutoPct = useCallback(async (p: number) => {
    setPct(p)
    const tok = await getToken().catch(() => null)
    if (!tok) return
    fetch(`${API_BASE}/users/me/preferences`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ teamFundingPct: p }),
    }).catch(() => {})
  }, [getToken])

  // Save on every move, so a ballot is never left half-changed.
  const saveRanking = useCallback(async (ranking: string[]) => {
    if (!favId || busy) return
    setBusy(true); setMyRanking(ranking)   // optimistic
    try {
      const tok = await getToken()
      await fetch(`${API_BASE}/teams/${favId}/facilities/ranking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ ranking }),
      })
      await load()   // refetch so the team order and project ranks reflect the change
    } finally { setBusy(false) }
  }, [favId, busy, getToken, load])

  if (loading) return <div style={{ color: '#94a3b8', padding: '20px', fontSize: '13px' }}>Loading facilities…</div>

  const me = data ? league.find(t => t.id === data.teamId) : undefined
  const tierColor = TIER_COLOR[me?.marketTier || 'MID_MARKET']
  const accent = me?.color || tierColor   // the team's own color tints the tab

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', color: '#e2e8f0', fontFamily: 'pressStart, monospace' }}>
      <style>{FAC_CSS}</style>
      {!favId && <div style={{ color: '#94a3b8', fontSize: '13px' }}>Pick a favorite team to manage its facilities.</div>}

      {data && (
        <>
          {/* readout panel — suppressed in band mode: the host's summary strip
              already carries Treasury / Appeal / Market, and showing them twice
              on one screen was the loudest part of "tacked on". */}
          {!band && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '1px',
            background: '#2c3a4d', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,.4)' }}>
            {([
              ['Market', TIER_SHORT[me?.marketTier || 'MID_MARKET'], tierColor, 'Fanbase size',
                'How big your fanbase is compared to the rest of the league, from SMALL up to MEGA.'],
              ['Appeal', appealRank(data.appeal), '#34d399', 'Facility quality',
                'How strong your facilities are overall, from your combined facility levels.'],
              ['Treasury', <><FloobitSymbol size={19} color="#fbbf24" />{data.treasury.toLocaleString()}</>, '#fbbf24',
                data.salaryEnabled ? <>Payroll <FloobitSymbol size={10} color="#94a3b8" />{(data.payroll ?? 0).toLocaleString()} a season</> : 'Project fund',
                treasuryTip(!!data.salaryEnabled)],
            // ⚠️ The value slot is a NODE now — Treasury carries the currency mark, and the
            // other two rows are plain strings, which a ReactNode accepts unchanged.
            ] as [string, React.ReactNode, string, React.ReactNode, string][]).map(([l, v, c, sub, tip]) => (
              <HoverTooltip key={l} content={tip} color={c}>
                <div style={{ background: '#1e293b', padding: '13px 15px', height: '100%', boxSizing: 'border-box' }}>
                  <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#94a3b8', fontWeight: 700 }}>{l}</div>
                  <div style={{ fontSize: '21px', fontWeight: 800, margin: '5px 0 2px', color: c,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{sub}</div>
                </div>
              </HoverTooltip>
            ))}
          </div>
          )}

          {data.salaryEnabled && (data.payrollOutlook?.shortBy ?? 0) > 0 && (
            <PayrollWarning shortBy={data.payrollOutlook!.shortBy} owed={data.payrollOutlook!.owedNextSeason} />
          )}

          {/* Treasury funding: season-end auto-deposit % + direct contribution, one row.
              Direct chips also count toward the patron rank + funding achievements. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', fontWeight: 700, marginRight: '4px' }}>Season-end deposit</span>
            {[0, 10, 25, 50, 75, 100].map(p => (
              <button key={p} className="facChip" onClick={() => setAutoPct(p)} style={{
                padding: '5px 11px', fontSize: '12px', fontWeight: pct === p ? 700 : 500, borderRadius: '5px',
                border: `1px solid ${pct === p ? accent : '#334155'}`, background: pct === p ? hex(accent, 0.13) : 'transparent',
                color: pct === p ? accent : '#cbd5e1',
              }}>{p}%</button>
            ))}
            <span style={{ width: '1px', height: '18px', background: '#334155', margin: '0 6px' }} />
            <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', fontWeight: 700, marginRight: '2px' }}>Add now</span>
            <FundChips onFund={(amt) => contribute(amt, 'treasury')} balance={balance} max={Number.MAX_SAFE_INTEGER} topGap={0} allowCustom />
          </div>

          {/* kanban: facilities | build order | your ranking */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))', gap: '16px', alignItems: 'start' }}>
            {/* current facilities */}
            <section>
              <SectionHead title="Team Facilities" hint="Fund facility upkeep" accent={accent} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
                {data.facilities.map(f => <FacilityTile key={f.key} f={f} accent={accent} balance={balance} onFund={(amt) => contribute(amt, 'upkeep', { facilityKey: f.key })} />)}
              </div>
            </section>

            {/* projects, in the team's build order */}
            <section>
              <SectionHead title="Build Order" hint="Fund any project directly" accent={BUILD} />
              {data.projects.length ? data.projects.map(p => {
                const fac = data.facilities.find(x => x.key === p.facilityKey)
                return <ProjectCard key={p.id} p={p} name={catalog[p.facilityKey] || p.facilityKey} fromLvl={fac?.level ?? p.targetLevel - 1} balance={balance} onFund={(amt) => contribute(amt, 'project', { projectId: p.id })} />
              }) : <div style={{ fontSize: '12.5px', color: '#7e93a8', padding: '12px 14px', borderRadius: '9px', border: '1px dashed #2c3a4d', background: 'rgba(20,29,41,.5)' }}>Every facility is at its top level.</div>}
            </section>

            {/* the fan's own ranked ballot */}
            <section>
              <SectionHead title="Your Ranking" hint="Order every facility by priority" accent={accent} />
              <RankingList
                facilities={rankFacs}
                ranking={myRanking || teamOrder}
                saved={!!myRanking}
                ballots={ballots}
                accent={accent}
                busy={busy}
                onChange={saveRanking}
              />
            </section>
          </div>

          {/* how funding works */}
          <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.55, background: '#15202d', border: '1px solid #2c3a4d', borderRadius: '9px', padding: '12px 15px' }}>
            <b style={{ color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '.06em', fontSize: '11px' }}>How funding works</b>
            <div style={{ marginTop: '6px' }}>
              Chip in to any facility's upkeep, any project, or the <span style={{ color: '#fbbf24' }}>Treasury</span> directly, or set a <span style={{ color: '#cbd5e1' }}>season-end deposit</span> above to send your leftover Floobits to the Treasury at season end. A project you fund in full builds right away. After the free agent draft, the Treasury pays {data.salaryEnabled && <><span style={{ color: '#fbbf24', fontWeight: 700 }}>player salaries first</span>, then </>}<span style={{ color: '#3b82f6', fontWeight: 700 }}>upkeep{data.salaryEnabled ? '' : ' first'}</span> so no facility slips a level, then builds <span style={{ color: '#a78bfa', fontWeight: 700 }}>projects in your fans' ranked order</span> until the money runs out. Each facility can go up one level a year. Unfunded upkeep drops a facility a level.
            </div>
          </div>
        </>
      )}

      {/* league readouts — tabbed (Facilities / Fanbase). In band mode these
          start closed: they're a LEAGUE view, and on your own team's page they
          were the largest thing on screen while answering a question nobody
          had asked yet. */}
      {band && !leagueOpen && (
        <button
          type="button"
          onClick={() => setLeagueOpen(true)}
          style={{
            font: 'inherit', alignSelf: 'flex-start', cursor: 'pointer',
            background: 'none', border: 'none', padding: '2px 0',
            fontSize: '13px', fontWeight: 700, color: '#cbd5e1',
            letterSpacing: '.04em',
          }}
        >Compare across the league &rarr;</button>
      )}
      {(!band || leagueOpen) && (
      <section>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', margin: '0 0 11px', borderBottom: '1px solid #1e293b' }}>
          {([['facilities', 'League Facilities'], ['fanbase', 'League Fanbase']] as const).map(([key, label]) => {
            const active = leagueTab === key
            return (
              <button key={key} onClick={() => setLeagueTab(key)} style={{
                font: 'inherit', cursor: 'pointer', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
                padding: '6px 2px', background: 'none', border: 'none', borderBottom: `2px solid ${active ? '#2dd4bf' : 'transparent'}`,
                color: active ? '#e2e8f0' : '#64748b', marginBottom: '-1px',
              }}>{label}</button>
            )
          })}
          <span style={{ fontSize: '11.5px', color: '#7e93a8' }}>
            {leagueTab === 'facilities' ? 'How strong each team\'s facilities are across the league' : 'How many fans each team has, which sets the Market tier'}
          </span>
        </div>
        {leagueTab === 'facilities'
          ? <AppealGraph teams={league} catalog={catalog} favId={favId} />
          : <FanGraph teams={league} favId={favId} />}
      </section>
      )}
    </div>
  )
}

function SectionHead({ title, hint, accent }: { title: string; hint: string; accent: string }) {
  return (
    <h2 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '.12em', color: '#cbd5e1', margin: '0 0 11px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <span style={{ width: '4px', height: '15px', borderRadius: '2px', background: accent, flexShrink: 0 }} />
      {title}<span style={{ fontSize: '12px', textTransform: 'none', letterSpacing: 0, color: '#7e93a8', fontWeight: 400 }}>{hint}</span>
    </h2>
  )
}

function FacilityTile({ f, accent, balance, onFund }: { f: Facility; accent: string; balance: number; onFund: (amt: number) => void }) {
  const perk = perkAt(f.key, f.level)
  const quip = quipAt(f.key, f.level)
  const covered = f.upkeepFunded >= f.upkeepCost
  const waived = f.builtThisYear ?? f.upgrading
  const c = waived ? BUILD : accent
  return (
    <div className="facRow" style={{
      background: '#1e293b',
      border: '1px solid #2c3a4d', borderTop: `2px solid ${c}`, borderRadius: '9px', padding: '12px 13px 13px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', lineHeight: 1.2 }}>
        {f.name} <span style={{ color: c }}>{roman(f.level)}</span>
      </div>
      <div style={{ ...QUIP_STYLE, minHeight: '32px', marginTop: '11px' }}>{quip}</div>
      <div style={{ fontSize: '12px', fontWeight: 700, color: perk ? '#e2e8f0' : '#5b6b7d', marginTop: '3px' }}>{perk || 'No bonus yet'}</div>
      {waived ? (
        <div style={{ fontSize: '12px', color: BUILD, fontWeight: 600, letterSpacing: '.03em', marginTop: '10px' }}>BUILT THIS YEAR | NO UPKEEP DUE</div>
      ) : covered ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', margin: '10px 0 5px' }}>
            <span>Upkeep</span><span><b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{f.upkeepCost}/season</b>, <span style={{ color: '#34d399', fontWeight: 700 }}>FUNDED</span></span>
          </div>
          <div style={{ height: '6px', background: '#334155', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: '100%', height: '100%', background: '#22c55e' }} />
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', margin: '10px 0 5px' }}>
            <span>Upkeep</span><b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{f.upkeepFunded}/{f.upkeepCost} per season</b>
          </div>
          <div style={{ height: '6px', background: '#334155', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${f.upkeepCost ? (f.upkeepFunded / f.upkeepCost) * 100 : 100}%`, height: '100%', background: '#3b82f6' }} />
          </div>
          <FundChips onFund={onFund} balance={balance} max={f.upkeepCost - f.upkeepFunded} />
        </>
      )}
    </div>
  )
}

function ProjectCard({ p, name, fromLvl, balance, onFund }: { p: Project; name: string; fromLvl: number; balance: number; onFund: (amt: number) => void }) {
  const pct = p.cost ? (p.funded / p.cost) * 100 : 0
  const full = p.funded >= p.cost
  return (
    <div style={{ background: '#1a1730', border: '1px solid #3a2d5c', borderTop: `2px solid ${BUILD}`, borderRadius: '9px', padding: '13px 15px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        {p.rank != null && <span style={{ fontSize: '12px', fontWeight: 800, color: BUILD }}>#{p.rank}</span>}
        <span style={{ fontSize: '14px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em' }}>{name} {roman(fromLvl)} → {roman(p.targetLevel)}</span>
      </div>
      <div style={{ ...QUIP_STYLE, marginTop: '8px' }}>{quipAt(p.facilityKey, p.targetLevel)}</div>
      <div style={{ fontSize: '12.5px', marginTop: '4px' }}>Unlocks: <span style={{ color: '#2dd4bf', fontWeight: 600 }}>{perkAt(p.facilityKey, p.targetLevel) || 'Foundational level'}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', margin: '11px 0 5px' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '.06em' }}>Build progress</span><b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{p.funded.toLocaleString()} / {p.cost.toLocaleString()}</b>
      </div>
      <div style={{ height: '10px', background: '#2e2552', borderRadius: '6px', overflow: 'hidden', border: '1px solid #3a2d5c' }}>
        <div className={full ? '' : 'facStripes'} style={{ width: `${pct}%`, height: '100%',
          background: full ? '#22c55e' : undefined }} />
      </div>
      {full ? <div style={{ fontSize: '12px', fontWeight: 700, color: BUILD, marginTop: '7px' }}>Funded, {BUILDS_AT_TEXT[p.buildsAt || 'next_season']}</div>
            : <FundChips onFund={onFund} balance={balance} max={p.cost - p.funded} />}
      {!full && p.buildsAt && p.buildsAt !== 'now' && (
        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '7px' }}>Already upgraded this year: {BUILDS_AT_TEXT[p.buildsAt]} once funded</div>
      )}
    </div>
  )
}

const BUILDS_AT_TEXT: Record<string, string> = {
  now: 'building now',
  payment_run: 'builds after the free agent draft',
  next_season: 'builds when next season starts',
}

// A fan's ranked ballot. Up/down buttons rather than drag, so it works by keyboard and on
// a phone. Every move saves at once.
function RankingList({ facilities, ranking, saved, ballots, accent, busy, onChange }: {
  facilities: RankFacility[]; ranking: string[]; saved: boolean; ballots: number
  accent: string; busy: boolean; onChange: (r: string[]) => void
}) {
  const byKey = Object.fromEntries(facilities.map(f => [f.key, f]))
  const order = ranking.filter(k => byKey[k])
  const move = (i: number, d: number) => {
    const j = i + d
    if (busy || j < 0 || j >= order.length) return
    const next = [...order]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  const arrow = (disabled: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: '12px', fontWeight: 700, width: '28px', height: '26px', borderRadius: '6px',
    cursor: disabled ? 'default' : 'pointer', border: `1px solid ${disabled ? '#1e293b' : '#2f4a6b'}`,
    background: disabled ? 'transparent' : '#15293f', color: disabled ? '#475569' : '#93c5fd',
  })
  return (
    <div>
      <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5, marginBottom: '10px' }}>
        {saved ? 'Your ranking counts toward the team\'s build order and stays until you change it.'
               : 'You haven\'t ranked yet. This is the team\'s current order; move anything to save your own.'}
        {' '}{ballots === 1 ? '1 fan has' : `${ballots} fans have`} ranked.
      </div>
      {order.map((k, i) => {
        const f = byKey[k]
        return (
          <div key={k} className="facRow" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px',
            background: '#1e293b', border: `1px solid ${i === 0 && saved ? accent : '#2c3a4d'}`, borderRadius: '9px', padding: '9px 11px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: accent, width: '22px' }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                {f.name} {f.maxed ? roman(f.level) : <>{roman(f.level)} → {roman(f.level + 1)}</>}
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>
                {f.maxed ? 'At its top level' : <>Build <b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{f.cost.toLocaleString()}</b>, then <b style={{ color: '#fbbf24' }}><FloobitSymbol size={11} color="#fbbf24" />{f.upkeep.toLocaleString()}/season</b></>}
              </div>
            </div>
            <button aria-label={`Move ${f.name} up`} disabled={busy || i === 0} onClick={() => move(i, -1)} style={arrow(busy || i === 0)}>&uarr;</button>
            <button aria-label={`Move ${f.name} down`} disabled={busy || i === order.length - 1} onClick={() => move(i, 1)} style={arrow(busy || i === order.length - 1)}>&darr;</button>
          </div>
        )
      })}
    </div>
  )
}

// per-team facility power meters, ranked by facility quality (Appeal)
function AppealGraph({ teams, catalog, favId }: { teams: LeagueTeam[]; catalog: Record<string, string>; favId: number | null }) {
  const keys = Object.keys(catalog)
  const sorted = [...teams].sort((a, b) => b.appeal - a.appeal)
  return (
    <div style={{ background: '#0f172a', border: '1px solid #1d2c3e', borderRadius: '10px', padding: '6px 4px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr 64px', gap: '12px', alignItems: 'center', padding: '5px 12px', borderBottom: '1px solid #1d2c3e' }}>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#7e93a8', fontWeight: 700 }}>Team</span>
        <span style={{ display: 'flex', gap: '4px' }}>
          {keys.map(k => <span key={k} style={{ flex: 1, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em', color: '#7e93a8', fontWeight: 700, textAlign: 'center' }}>{SHORT_FAC[k] || catalog[k]}</span>)}
        </span>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#34d399', fontWeight: 700, textAlign: 'right' }}>Appeal</span>
      </div>
      {sorted.map((t) => {
        const isFav = t.id === favId
        const rc = t.color || TIER_COLOR[t.marketTier] || '#2dd4bf'
        const tip = (
          <div style={{ textAlign: 'left', fontSize: '13px', lineHeight: 1.6, fontFamily: 'pressStart, monospace' }}>
            <div style={{ fontWeight: 700, color: t.color, marginBottom: '4px' }}>{t.city} {t.name}</div>
            <div>Appeal: <strong>{appealRank(t.appeal)}</strong></div>
            {keys.map(k => <div key={k}>{catalog[k]}: <strong>{t.levels[k] ? roman(t.levels[k]) : 'Not built'}</strong></div>)}
          </div>
        )
        return (
          <Link key={t.id} to={`/team/${t.id}`} className="facRow" style={{ display: 'grid', gridTemplateColumns: '56px 1fr 64px', gap: '12px', alignItems: 'center', padding: '5px 12px', borderRadius: '4px', textDecoration: 'none',
            background: isFav ? hex(t.color, 0.1) : 'transparent', boxShadow: isFav ? `inset 0 0 0 1px ${hex(t.color, 0.5)}` : 'none' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#cbd5e1' }}>{t.abbr}</span>
            <HoverTooltip content={tip} color={t.color}>
              <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {keys.map(k => {
                  const lvl = t.levels[k] || 0
                  return <span key={k} style={{ flex: 1, display: 'flex', gap: '2px' }}>
                    {[1, 2, 3, 4, 5].map(i => {
                      const on = i <= lvl
                      return <span key={i} style={{ flex: 1, height: '9px', borderRadius: '2px', background: on ? rc : '#16273a' }} />
                    })}
                  </span>
                })}
              </span>
            </HoverTooltip>
            <span style={{ fontSize: '12px', fontWeight: 800, color: '#34d399', textAlign: 'right' }}>{appealRank(t.appeal)}</span>
          </Link>
        )
      })}
    </div>
  )
}

// fan-count bars
function FanGraph({ teams, favId }: { teams: LeagueTeam[]; favId: number | null }) {
  const sorted = [...teams].sort((a, b) => b.fanCount - a.fanCount)
  const maxFans = Math.max(1, ...sorted.map(t => t.fanCount))
  return (
    <div style={{ background: '#0f172a', border: '1px solid #1d2c3e', borderRadius: '10px', padding: '8px 6px' }}>
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', padding: '2px 12px 8px', fontSize: '11px', color: '#94a3b8', fontWeight: 700, letterSpacing: '.04em' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ display: 'inline-block', width: '14px', height: '9px', borderRadius: '2px', background: '#64748b' }} /> Active this season
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ display: 'inline-block', width: '14px', height: '9px', borderRadius: '2px', background: hex('#64748b', 0.28) }} /> Total fans
        </span>
      </div>
      {sorted.map(t => {
        const isFav = t.id === favId
        const active = Math.min(t.fanCount, t.activeFanCount ?? 0)
        const activePct = t.fanCount > 0 ? (active / t.fanCount) * 100 : 0
        const tip = (
          <div style={{ textAlign: 'left', fontSize: '13px', lineHeight: 1.6, fontFamily: 'pressStart, monospace' }}>
            <div style={{ fontWeight: 700, color: t.color, marginBottom: '4px' }}>{t.city} {t.name}</div>
            <div>Total fans: <strong>{t.fanCount.toLocaleString()}</strong></div>
            <div>Active this season: <strong>{active.toLocaleString()}</strong></div>
            <div>Market: <strong style={{ color: TIER_COLOR[t.marketTier] }}>{TIER_SHORT[t.marketTier]}</strong></div>
          </div>
        )
        return (
          <Link key={t.id} to={`/team/${t.id}`} className="facRow" style={{ display: 'grid', gridTemplateColumns: '56px 1fr 46px', gap: '12px', alignItems: 'center', padding: '4px 12px', borderRadius: '4px', textDecoration: 'none',
            background: isFav ? hex(t.color, 0.1) : 'transparent', boxShadow: isFav ? `inset 0 0 0 1px ${hex(t.color, 0.5)}` : 'none' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#cbd5e1' }}>{t.abbr}</span>
            <HoverTooltip content={tip} color={t.color}>
              <span style={{ display: 'block', height: '11px' }}>
                {/* faint bar = TOTAL fans; the solid inner fill = ACTIVE fans this season */}
                <span style={{ display: 'block', height: '100%', width: `${(t.fanCount / maxFans) * 100}%`, background: hex(t.color, 0.28), borderRadius: '3px', minWidth: '2px' }}>
                  <span style={{ display: 'block', height: '100%', width: `${activePct}%`, background: t.color, borderRadius: '3px', minWidth: active > 0 ? '2px' : '0' }} />
                </span>
              </span>
            </HoverTooltip>
            <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#94a3b8', textAlign: 'right' }}>{t.fanCount}</span>
          </Link>
        )
      })}
    </div>
  )
}

export default FacilitiesSection
